import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Eye, Info, CheckCircle2, AlertCircle } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';

const API = `${BASE}/api`;

// ── 타입 ────────────────────────────────────────────────────
interface ContextOption {
  id: number;
  category: string;
  itemKey: string;
  itemLabel: string;
  tooltipText: string | null;
  multiYn: 'Y' | 'N';
  requiredYn: 'Y' | 'N';
  sortOrder: number;
}

interface ContextOptionsGrouped {
  [category: string]: ContextOption[];
}

interface UserContextProfile {
  useYn: 'Y' | 'N';
  contextCodes: Record<string, string | string[]>;
  requiredKeywords: string;
  forbiddenKeywords: string;
  customInstruction: string;
  engYn: 'Y' | 'N';
}

interface PreviewResult {
  prompt: string;
}

// 카테고리 표시명 매핑
const CATEGORY_LABELS: Record<string, string> = {
  INDUSTRY_TYPE: '업종 유형',
  SITE_TYPE: '사이트 유형',
  SERVICE_TARGET: '서비스 대상',
  IMPACT_INFO_TYPE: '강조 정보 유형',
  IMAGE_TYPE: '이미지 타입',
  OUTPUT_TONE: 'ALT 출력 톤/문체',
  MAX_LENGTH: '길이 제한',
  ENDING_STYLE: '종결 어미',
  ALT_PURPOSE: 'ALT 생성 목적',
  FORBIDDEN_INFO: '제외 정보',
};

const CATEGORY_ORDER = [
  'INDUSTRY_TYPE',
  'SITE_TYPE',
  'SERVICE_TARGET',
  'IMPACT_INFO_TYPE',
  'IMAGE_TYPE',
  'OUTPUT_TONE',
  'MAX_LENGTH',
  'ENDING_STYLE',
  'ALT_PURPOSE',
  'FORBIDDEN_INFO',
];

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const safeReadJson = async (res: Response, fallbackMessage: string) => {
  const contentType = res.headers.get('content-type') || '';
  const bodyText = await res.text();

  if (!res.ok) {
    let serverMessage = '';
    if (contentType.includes('application/json') && bodyText) {
      try {
        const parsed = JSON.parse(bodyText);
        serverMessage = parsed?.message || parsed?.error?.message || parsed?.data?.message || '';
      } catch {
        serverMessage = '';
      }
    }
    if (!serverMessage && bodyText && !bodyText.trim().startsWith('<')) {
      serverMessage = bodyText.slice(0, 200);
    }
    throw new Error(serverMessage || `${fallbackMessage} (HTTP ${res.status})`);
  }

  if (!contentType.includes('application/json')) {
    if (bodyText.trim().startsWith('<')) {
      throw new Error('API 응답이 HTML로 반환되었습니다. 인증 만료 또는 API 경로 설정을 확인해주세요.');
    }
    throw new Error('API 응답 형식이 올바르지 않습니다.');
  }

  if (!bodyText) {
    return null;
  }

  try {
    return JSON.parse(bodyText);
  } catch {
    throw new Error('API JSON 파싱에 실패했습니다.');
  }
};

export default function ContextProfilePage() {
  useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  const [optionsGrouped, setOptionsGrouped] = useState<ContextOptionsGrouped>({});
  const [profile, setProfile] = useState<UserContextProfile>({
    useYn: 'N',
    contextCodes: {},
    requiredKeywords: '',
    forbiddenKeywords: '',
    customInstruction: '',
    engYn: 'N',
  });
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ key: string; text: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [optRes, profileRes] = await Promise.all([
        fetch(`${API}/user/alt-profiles/context-options`, { headers: getAuthHeaders() }),
        fetch(`${API}/user/alt-profiles/me`, { headers: getAuthHeaders() }),
      ]);

      if (optRes.ok) {
        const raw = await safeReadJson(optRes, '코드 옵션 조회에 실패했습니다.');
        let opts: ContextOption[] = [];
        if (Array.isArray(raw)) {
          opts = raw;
        } else if (raw && typeof raw === 'object') {
          const inner = raw.data ?? raw;
          if (Array.isArray(inner)) {
            opts = inner;
          } else if (typeof inner === 'object') {
            // grouped format: { CATEGORY: [...items] } — flatten to array
            for (const v of Object.values(inner)) {
              if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && 'category' in (v[0] as object)) {
                opts = (Object.values(inner) as ContextOption[][]).flat();
                break;
              }
            }
          }
        }
        const grouped: ContextOptionsGrouped = {};
        opts.forEach(o => {
          if (!grouped[o.category]) grouped[o.category] = [];
          grouped[o.category].push(o);
        });
        if (opts.length === 0) {
          setError('컨텍스트 옵션을 불러왔지만 항목이 없습니다. 관리자에게 문의하세요.');
        }
        setOptionsGrouped(grouped);
      } else {
        await safeReadJson(optRes, '코드 옵션 조회에 실패했습니다.');
      }

      if (profileRes.status === 404) {
        setIsNew(true);
      } else if (profileRes.ok) {
        const raw = await safeReadJson(profileRes, '프로필 조회에 실패했습니다.');
        const p = raw.data ?? raw;
        // Normalize contextCodes: ensure multi-select values are always arrays
        const rawCodes: Record<string, string | string[]> = p.contextCodes ?? {};
        const normalizedCodes: Record<string, string | string[]> = {};
        for (const [k, v] of Object.entries(rawCodes)) {
          normalizedCodes[k] = v;
        }
        setProfile({
          useYn: p.useYn ?? 'Y',
          contextCodes: normalizedCodes,
          requiredKeywords: p.requiredKeywords ?? '',
          forbiddenKeywords: p.forbiddenKeywords ?? '',
          customInstruction: p.customInstruction ?? '',
          engYn: p.engYn ?? 'N',
        });
        setIsNew(false);
      } else {
        await safeReadJson(profileRes, '프로필 조회에 실패했습니다.');
      }
    } catch (e: any) {
      setError(e.message || '데이터를 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 단일선택 변경
  const handleSingleChange = (category: string, value: string) => {
    setProfile(prev => ({
      ...prev,
      contextCodes: {
        ...prev.contextCodes,
        [category]: value,
      },
    }));
  };

  // 복수선택 변경
  const handleMultiChange = (category: string, value: string, checked: boolean) => {
    setProfile(prev => {
      const raw = prev.contextCodes[category];
      const current: string[] = Array.isArray(raw) ? raw : (raw ? [String(raw)] : []);
      const updated = checked
        ? [...current, value]
        : current.filter(v => v !== value);
      const next = { ...prev.contextCodes };
      if (updated.length === 0) {
        delete next[category];
      } else {
        next[category] = updated;
      }
      return { ...prev, contextCodes: next };
    });
  };

  const validate = (): string | null => {
    for (const category of CATEGORY_ORDER) {
      const options = optionsGrouped[category] ?? [];
      const isRequired = options.some(o => o.requiredYn === 'Y');
      if (isRequired && !profile.contextCodes[category]) {
        return `"${CATEGORY_LABELS[category] || category}" 항목은 필수입니다.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const validErr = validate();
    if (validErr) { setError(validErr); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // B/E contextCodes 타입: Map<String, Object> — 배열/단일값 그대로 전송
      const payload = {
        useYn: profile.useYn,
        contextCodes: profile.contextCodes,
        requiredKeywords: profile.requiredKeywords || null,
        forbiddenKeywords: profile.forbiddenKeywords || null,
        customInstruction: profile.customInstruction || null,
        engYn: profile.engYn,
      };
      const res = await fetch(`${API}/user/alt-profiles`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        let msg = `저장에 실패했습니다. (HTTP ${res.status})`;
        try {
          const err = JSON.parse(text);
          msg = err?.message || err?.error?.message || err?.data?.message || text || msg;
        } catch {
          if (text) msg = text.slice(0, 200);
        }
        throw new Error(msg);
      }
      setSuccess('컨텍스트 프로필이 저장되었습니다.');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewText(null);
    setError(null);
    try {
      const res = await fetch(`${API}/user/alt-profiles/preview`, { headers: getAuthHeaders() });
      const raw = await safeReadJson(res, '미리보기 조회에 실패했습니다.');
      const data: PreviewResult = raw.data ?? raw;
      setPreviewText(data.prompt);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-500">불러오는 중...</span>
      </div>
    );
  }

  const orderedCategories = CATEGORY_ORDER.filter(c => optionsGrouped[c]);

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '컨텍스트 프로필'}
        description="업종·사이트 특성을 등록하면 AI가 더 정확한 ALT를 생성합니다."
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-300">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* 프로필 사용 여부 */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold">프로필 사용 여부</p>
              <p className="text-sm text-gray-500 mt-0.5">
                OFF 설정 시 ALT 생성에 이 프로필이 적용되지 않습니다.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${profile.useYn === 'Y' ? 'text-green-600' : 'text-gray-400'}`}>
                {profile.useYn === 'Y' ? '사용 중' : '사용 안 함'}
              </span>
              <button
                type="button"
                onClick={() => setProfile(prev => ({ ...prev, useYn: prev.useYn === 'Y' ? 'N' : 'Y' }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  profile.useYn === 'Y' ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    profile.useYn === 'Y' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 선택 항목 탭 */}
      <Card>
        <CardHeader>
          <CardTitle>코드 선택</CardTitle>
          <CardDescription>
            각 항목을 선택하면 AI 프롬프트에 자동 반영됩니다.
            <span className="text-red-500 ml-1">*</span> 표시는 필수입니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {orderedCategories.map(category => {
            const options = optionsGrouped[category];
            const isMulti = options[0]?.multiYn === 'Y';
            const isRequired = options.some(o => o.requiredYn === 'Y');
            const rawVal = profile.contextCodes[category];
            const selectedSingle = !isMulti ? (Array.isArray(rawVal) ? rawVal[0] : String(rawVal ?? '')) : '';
            const selectedMulti: string[] = isMulti
              ? (Array.isArray(rawVal) ? rawVal : (rawVal ? [String(rawVal)] : []))
              : [];

            return (
              <div key={category} className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label className="text-sm font-semibold">
                    {CATEGORY_LABELS[category] || category}
                  </Label>
                  {isRequired && <span className="text-red-500 text-xs">*</span>}
                  <Badge className="text-xs px-1 py-0 ml-1 border border-gray-400 text-gray-600 bg-transparent">
                    {isMulti ? '복수선택' : '단일선택'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-3">
                  {options.map(opt => {
                    const isChecked = isMulti
                      ? selectedMulti.includes(opt.itemKey)
                      : (selectedSingle !== '' && selectedSingle === opt.itemKey);

                    return (
                      <label
                        key={opt.itemKey}
                        className="flex items-center gap-1.5 cursor-pointer group"
                        onMouseEnter={() => opt.tooltipText && setTooltip({ key: `${category}-${opt.itemKey}`, text: opt.tooltipText })}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        <input
                          type={isMulti ? 'checkbox' : 'radio'}
                          name={category}
                          value={opt.itemKey}
                          checked={isChecked}
                          onChange={e => {
                            if (isMulti) {
                              handleMultiChange(category, opt.itemKey, e.target.checked);
                            } else {
                              handleSingleChange(category, opt.itemKey);
                            }
                          }}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm">{opt.itemLabel}</span>
                        {opt.tooltipText && (
                          <Info className="w-3 h-3 text-gray-400 group-hover:text-blue-500" />
                        )}
                        {/* 툴팁 */}
                        {tooltip?.key === `${category}-${opt.itemKey}` && (
                          <div className="absolute z-50 mt-1 max-w-xs bg-gray-800 text-white text-xs rounded px-2 py-1 shadow-lg pointer-events-none">
                            {tooltip.text}
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* 자유 입력 */}
      <Card>
        <CardHeader>
          <CardTitle>자유 입력</CardTitle>
          <CardDescription>추가 지시사항을 직접 입력하세요.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>필수 포함어</Label>
              <Input
                placeholder="예: 쿠팡, 무료배송 (콤마로 구분)"
                value={profile.requiredKeywords}
                onChange={e => setProfile(prev => ({ ...prev, requiredKeywords: e.target.value }))}
              />
              <p className="text-xs text-gray-500">ALT 텍스트에 반드시 포함할 단어</p>
            </div>
            <div className="space-y-1">
              <Label>금지어</Label>
              <Input
                placeholder="예: 경쟁사명, 민감용어 (콤마로 구분)"
                value={profile.forbiddenKeywords}
                onChange={e => setProfile(prev => ({ ...prev, forbiddenKeywords: e.target.value }))}
              />
              <p className="text-xs text-gray-500">ALT 텍스트에 포함하지 않을 단어</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label>추가 지시사항</Label>
            <textarea
              className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="AI에 전달할 추가 설명이나 요구사항을 자유롭게 입력하세요."
              value={profile.customInstruction}
              onChange={e => setProfile(prev => ({ ...prev, customInstruction: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="engYn"
              checked={profile.engYn === 'Y'}
              onChange={e => setProfile(prev => ({ ...prev, engYn: e.target.checked ? 'Y' : 'N' }))}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="engYn" className="text-sm cursor-pointer select-none">
              영어 병기 (한국어 ALT 옆에 영문 병기)
            </label>
          </div>
        </CardContent>
      </Card>

      {/* 미리보기 결과 */}
      {previewText && (
        <Card className="bg-gray-50 border-gray-300">
          <CardHeader>
            <CardTitle className="text-base">📋 커스텀 컨텍스트 미리보기</CardTitle>
            <CardDescription>AI에 전달될 프롬프트 내용입니다.</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-white rounded border p-4 leading-relaxed">
              {previewText}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* 액션 버튼 */}
      <div className="flex flex-wrap gap-3 justify-end">
        <Button
          variant="outline"
          onClick={handlePreview}
          disabled={previewing || isNew}
          title={isNew ? '저장 후 미리보기 가능합니다' : ''}
        >
          {previewing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />미리보기 중...</>
          ) : (
            <><Eye className="w-4 h-4 mr-2" />커스텀 컨텍스트 미리보기</>
          )}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />저장 중...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />저장</>
          )}
        </Button>
      </div>
    </PageContainer>
  );
}
